import {
  ClockInCheckboxCheckedSVG,
  ClockInCheckboxUncheckedSVG,
  ClockInSVG,
  ClockOutSVG,
  EditSVG,
  VerticalDividerSVG,
} from "@/assets/svg";
import { SelectWithRadio } from "@/components/form";
import { useAppDispatch, useAppSelector } from "@/hooks";
import {
  createTimeEntry,
  selectSelectedProject,
  selectTeamMemberId,
  setProject,
} from "@/store/slices/timeEntrySlice";
import {
  selectActiveEntry,
  selectElapsedTime,
  selectIsTimerActive,
  startTimer as startTimerAction,
  stopTimer as stopTimerAction,
  updateActiveEntry,
  updateElapsedTime,
  updateStartTime,
} from "@/store/slices/timerSlice";
import { errorToast, successToast } from "@/utils/toast";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { TimeEntryRow } from "./TimeEntryTable";
import { getProjectDetail } from "@/store/slices/projectSlice";
import { ProjectList } from "@/types/project.model";
import Modal from "@/components/Modal";

// Define a separate interface for ActiveTimeEntry data
export interface ActiveTimeEntryData {
  id: string;
  clockInDate: string;
  clockInTime: string;
  clockOutDate: string;
  clockOutTime: string;
  duration: string;
  serviceItem: string;
  billable: boolean;
  notes: string;
}

interface ActiveTimeEntryProps {
  // Initial data for the entry
  initialEntry: ActiveTimeEntryData;
  // Service items for dropdown
  serviceItems: { value: string; label: string }[];
}

const ActiveTimeEntry: React.FC<ActiveTimeEntryProps> = ({
  initialEntry,
  serviceItems,
}) => {
  const dispatch = useAppDispatch();

  // Get timer state from Redux
  const isTimerActive = useAppSelector(selectIsTimerActive);
  const reduxActiveEntry = useAppSelector(selectActiveEntry);
  const reduxElapsedTime = useAppSelector(selectElapsedTime);
  const teamMemberId = useAppSelector(selectTeamMemberId);
  const selectedProject = useAppSelector(selectSelectedProject);

  // Internal state for the active entry - initialized from Redux if available
  const [entry, setEntry] = useState<ActiveTimeEntryData>(
    reduxActiveEntry || initialEntry
  );

  // State for exceeding hours modal
  const [isExceedingHoursModalOpen, setIsExceedingHoursModalOpen] =
    useState<boolean>(false);
  const [entryToSubmit, setEntryToSubmit] =
    useState<ActiveTimeEntryData | null>(null);

  // State for inline editing clock in time
  const [isEditingClockIn, setIsEditingClockIn] = useState<boolean>(false);
  const [tempClockInTime, setTempClockInTime] = useState<string>("");

  // Timer reference for UI updates
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize with the initial entry or from Redux state
  useEffect(() => {
    if (reduxActiveEntry) {
      // If we have an active entry in Redux, use that
      setEntry(reduxActiveEntry);
      if (isTimerActive) {
        // Start UI timer for updates
        startUITimer();
      }
    } else if (initialEntry.clockInDate && initialEntry.clockInTime) {
      // If initialEntry has clock-in data but no Redux state, start a new timer
      setEntry(initialEntry);
      startTimer();
    }
  }, [initialEntry, reduxActiveEntry, isTimerActive]);

  // Update Redux elapsed time every second
  useEffect(() => {
    if (isTimerActive) {
      const intervalId = setInterval(() => {
        dispatch(updateElapsedTime());
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [isTimerActive, dispatch]);

  // Use callback for the beforeunload handler
  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (isTimerActive) {
        // Standard for most browsers
        event.preventDefault();
        // For older browsers
        const message =
          "You have an active time entry. Are you sure you want to leave?";
        event.returnValue = message;
        return message;
      }
    },
    [isTimerActive]
  );

  // Block navigation when timer is active
  useEffect(() => {
    // Add the event listener
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Clean up
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [handleBeforeUnload, isTimerActive]);

  // Handle service item change
  const handleServiceItemChange = (value: string) => {
    const updatedEntry = { ...entry, serviceItem: value };
    setEntry(updatedEntry);

    // Update Redux state if timer is active
    if (isTimerActive) {
      dispatch(updateActiveEntry({ serviceItem: value }));
    }
  };

  // Handle billable change
  const handleBillableChange = (checked: boolean) => {
    const updatedEntry = { ...entry, billable: checked };
    setEntry(updatedEntry);

    // Update Redux state if timer is active
    if (isTimerActive) {
      dispatch(updateActiveEntry({ billable: checked }));
    }
  };

  // Handle notes change
  const handleNotesChange = (value: string) => {
    const updatedEntry = { ...entry, notes: value };
    setEntry(updatedEntry);

    // Update Redux state if timer is active
    if (isTimerActive) {
      dispatch(updateActiveEntry({ notes: value }));
    }
  };

  // Handle clock in time update with validation
  const handleClockInTimeUpdate = () => {
    if (!tempClockInTime) {
      setIsEditingClockIn(false);
      return;
    }

    // Validate: Clock in time should not be in the future
    const clockInDate = moment(entry.clockInDate, "MM/DD/YYYY").format("YYYY-MM-DD");
    const newClockInDateTime = moment(`${clockInDate} ${tempClockInTime}`, "YYYY-MM-DD HH:mm");
    const now = moment();

    if (newClockInDateTime.isAfter(now)) {
      errorToast(
        "Invalid Time",
        "Clock in time cannot be in the future. Please select a valid time."
      );
      setIsEditingClockIn(false);
      return;
    }

    // Update entry with new clock in time
    const updatedEntry = {
      ...entry,
      clockInTime: tempClockInTime,
    };
    setEntry(updatedEntry);

    // Update Redux state and recalculate timer if active
    if (isTimerActive) {
      dispatch(updateActiveEntry({ clockInTime: tempClockInTime }));
      
      // Recalculate start time based on new clock in time
      const newStartTimeMs = newClockInDateTime.valueOf();
      dispatch(updateStartTime(newStartTimeMs));
    }

    successToast("Success", "Clock in time updated");
    setIsEditingClockIn(false);
  };

  // Handle success when entry is completed
  const handleSuccess = (updatedEntry: ActiveTimeEntryData) => {
    // Map the ActiveTimeEntryData to the format expected by the API
    const timeEntryPayload: TimeEntryRow = {
      ...updatedEntry,
      entry: "REALTIME", // This is a real-time entry, not manual
      actions: "",
    };

    // Dispatch the createTimeEntry action to save to the API
    dispatch(createTimeEntry(timeEntryPayload))
      .unwrap()
      .then(() => {
        // Stop the timer when API call is successful
        stopTimer();

        // Update local state
        setEntry(updatedEntry);

        // Show success message
        successToast("Success", "Time entry submitted successfully.");

        // Fetch the latest project details from the API instead of using table data
        dispatch(getProjectDetail(selectedProject.id))
          .unwrap()
          .then((projectData: ProjectList) => {
            dispatch(
              setProject({ projectId: selectedProject.id, projectData })
            );
          })
          .catch((error: Error) => {
            console.error("Failed to fetch project details:", error);
          });
      })
      .catch((error: string) => {
        // Show error message
        // errorToast("Error", error || "Failed to save time entry");
        console.error("Error saving time entry:", error);

        // Don't stop the timer or close the component on error
        // This allows the user to try again
      });
  };

  // Start the timer in Redux
  const startTimer = () => {
    // Dispatch action to start timer in Redux
    dispatch(startTimerAction(entry));

    // Start UI timer for updates
    startUITimer();
  };

  // Start UI timer for updating the display
  const startUITimer = () => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Set up interval to update UI every second
    timerRef.current = setInterval(() => {
      // This will trigger a re-render to show updated time
      setEntry((prev) => ({ ...prev }));
    }, 1000);
  };

  // Stop the timer
  const stopTimer = () => {
    // Clear UI timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Dispatch action to stop timer in Redux
    dispatch(stopTimerAction());
  };

  // Format the elapsed time for display
  const formatElapsedTime = (): string => {
    if (!isTimerActive) return entry.duration || "00:00:00";

    // Use moment.duration to format the elapsed time
    const duration = moment.duration(reduxElapsedTime);
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  // Clean up timer on component unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Check if duration exceeds remaining hours
  const checkIfDurationExceedsRemainingHours = (duration: string): boolean => {
    if (!selectedProject) return false;

    // Parse the duration (format: HH:MM)
    const durationParts = duration.split(":");
    const hours = parseInt(durationParts[0], 10) || 0;
    const minutes = parseInt(durationParts[1], 10) || 0;

    // Convert to hours
    const durationHours = hours + minutes / 60;

    // Get remaining hours from project
    const remainingHours =
      selectedProject.tracker?.total_esti_hrs - selectedProject.util_hrs;

    // Check if duration exceeds remaining hours
    return durationHours > remainingHours;
  };

  // Handle clock out
  const handleClockOut = () => {
    const now = moment();
    const formattedDate = now.format("YYYY-MM-DD");
    const formattedTime = now.format("hh:mm A");

    // Get formatted duration
    const formattedDuration = formatElapsedTime();

    const updatedEntry = {
      ...entry,
      user_id: teamMemberId,
      project_id: selectedProject.id,
      clockInDate: moment(entry.clockInDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
      clockInTime: moment(entry.clockInTime, "HH:mm").format("hh:mm A"),
      clockOutDate: formattedDate,
      clockOutTime: formattedTime,
      duration: formattedDuration,
    };

    if (!updatedEntry.serviceItem) {
      errorToast(
        "Service selection required",
        "Please select a service before checking out"
      );
      return;
    }

    // Check if duration exceeds remaining hours
    if (checkIfDurationExceedsRemainingHours(formattedDuration)) {
      // Store entry for later submission if user confirms
      setEntryToSubmit(updatedEntry);
      // Show exceeding hours modal
      setIsExceedingHoursModalOpen(true);
      return;
    }

    // If not exceeding, submit directly
    handleSuccess(updatedEntry);
  };

  return (
    <div className="border border-[#E0E0E0] rounded-lg">
      <div className="bg-[#F9FBFF] px-5 py-3">
        <h3 className="text-[16px] font-medium text-[#1E1E1E]">Clocked in</h3>
      </div>
      <div className="p-5">
        <div className="mb-4">
          <div className="flex gap-4">
            <div>
              <p className="text-md font-medium text-[#6C6C6C] mb-1">
                Clock in date
              </p>
              <div className="text-[20px] font-semibold">
                {entry.clockInDate}
              </div>
            </div>

            <VerticalDividerSVG className="mx-[30px]" />

            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="text-md font-medium text-[#6C6C6C]">
                  Clock in time
                </p>
                <button
                  onClick={() => {
                    setTempClockInTime(entry.clockInTime);
                    setIsEditingClockIn(true);
                  }}
                  className="p-1 hover:bg-gray-100 rounded cursor-pointer transition-colors"
                  title="Edit clock in time"
                >
                  {isEditingClockIn ? (
                    <ClockInSVG className="w-4 h-4" style={{ fill: '#73B5C5' }} />
                  ) : (
                    <EditSVG className="w-4 h-4" />
                  )}
                </button>
              </div>
              {isEditingClockIn ? (
                <input
                  type="time"
                  value={tempClockInTime}
                  onChange={(e) => setTempClockInTime(e.target.value)}
                  onBlur={handleClockInTimeUpdate}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.currentTarget.blur();
                    } else if (e.key === "Escape") {
                      setIsEditingClockIn(false);
                    }
                  }}
                  autoFocus
                  className="text-[20px] font-semibold px-2 py-1 border border-[#73B5C5] rounded-md focus:outline-none focus:border-[#73B5C5] w-[120px]"
                />
              ) : (
                <div className="text-[20px] font-semibold">
                  {entry.clockInTime}
                </div>
              )}
            </div>

            <VerticalDividerSVG className="mx-[30px]" />

            <div>
              <p className="text-md font-medium text-[#6C6C6C] mb-1">
                Duration
              </p>
              <div className="text-[20px] font-semibold">
                {isTimerActive ? formatElapsedTime() : entry.duration} hrs
              </div>
            </div>
          </div>
        </div>

        <div className="w-full mb-14">
          <table className="w-full">
            <thead className="bg-[#F4F7F9]">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-[#1E1E1E] w-[310px]">
                  Service item
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-[#1E1E1E] w-[80px]">
                  Billable
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[14px] text-[#1E1E1E] w-[676px]">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b-1 border-l-1 border-r-1 border-[#E0E0E0]">
                <td className="text-left border-r-1 border-[#E0E0E0]">
                  <SelectWithRadio
                    label=""
                    options={serviceItems}
                    value={entry.serviceItem}
                    onChange={(value) => handleServiceItemChange(value)}
                    placeholder="Select a service"
                    // showAbove={false}
                    className="w-full border-0"
                    labelClassName="font-medium"
                  />
                </td>
                <td className="text-left py-3 px-4 border-r-1 border-[#E0E0E0]">
                  <div
                    className="cursor-pointer"
                    onClick={() => handleBillableChange(!entry.billable)}
                  >
                    {entry.billable ? (
                      <ClockInCheckboxCheckedSVG />
                    ) : (
                      <ClockInCheckboxUncheckedSVG />
                    )}
                  </div>
                </td>
                <td className="text-left py-3 px-4 border-[#E0E0E0]">
                  <textarea
                    className="w-full focus:outline-none text-[#1E1E1E] text-[14px] font-medium"
                    placeholder="Add notes"
                    value={entry.notes}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    style={{
                      minHeight: "18px",
                      height: "18px",
                      resize: "none",
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = "18px";
                      target.style.height = target.scrollHeight + "px";
                    }}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-3">
          <button
            className="h-[34px] w-[150px] px-3 bg-[#D5D56C] text-black rounded-[4px] flex justify-center items-center gap-1 hover:bg-[#c8c866] transition-colors cursor-pointer"
            onClick={handleClockOut}
          >
            Clock out
            <ClockOutSVG />
          </button>
        </div>
      </div>

      {/* Modal for exceeding hours confirmation */}
      <Modal
        isOpen={isExceedingHoursModalOpen}
        onClose={() => setIsExceedingHoursModalOpen(false)}
        title="Exceeding Hours"
        width="min(95%, 550px)"
      >
        <div className="py-4">
          <p className="text-center text-[18px] font-[500] text-[#212121] mb-6">
            The clocked time exceeds the project's estimated hours. Do you still
            want to proceed?
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                if (entryToSubmit) {
                  handleSuccess(entryToSubmit);
                }
                setIsExceedingHoursModalOpen(false);
              }}
              className="px-3 py-2 w-[120px] text-[14px] font-medium h-[36px] bg-[#73B5C5] text-white rounded-md hover:bg-[#5da7b8] transition-colors cursor-pointer"
            >
              Yes, add time
            </button>
            <button
              onClick={() => setIsExceedingHoursModalOpen(false)}
              className="px-3 py-2 w-[98px] h-[36px] text-[14px] font-medium bg-[#F5F5F5] text-gray-800 rounded-md hover:bg-gray-200 transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ActiveTimeEntry;
